"use client";

import { useEffect, useMemo, useRef, useState } from "react";
import { useReducedMotion } from "framer-motion";
import { cn } from "@/lib/utils";
import type { PageTradingCompetitionSchema } from "@/sanity/pages/PageTradingCompetitionTemplate";
import SelectionHighlight, { type SelectionGeometry } from "./SelectionHighlight";
import BreakLines from "./BreakLines";
import TradingCompetitionCountdown from "./TradingCompetitionCountdown";
import { useCtaHref } from "./ctaHref";
import PodiumCard from "./leaderboard/PodiumCard";
import LeaderboardTable from "./leaderboard/LeaderboardTable";
import { useLiveBoard } from "./leaderboard/useLiveBoard";
import LiveBoard from "./leaderboard/LiveBoard";
import { formatPrize, maskName, prizeForRank, publicPoints, useLeaderboardPreview } from "./leaderboard/board";
import { createDummyBoard } from "./leaderboard/dummyBoard";
import { useApiBoard } from "./leaderboard/apiBoard";
import { at, useReveal } from "./motion";

/** Figma's text-box-trim on the button label (Chrome 133+, Safari 18.2+). */
const CAP_TRIM = "[text-box-trim:trim-both] [text-box-edge:cap_alphabetic]";

/** "Leaderboard" selection: Figma 193×37 at 34px (mobile), 296×53 at 48px (desktop), measured against the glyphs. */
const TITLE_SELECTION = {
  mobile: {
    font: 34,
    box: [-3.1, -4.99, 193, 37],
    word: 182.31,
    handles: [[-0.75, 0, 1.5, 33], [-2, 33, 4, 4], [191, 0, 4, 4], [192.25, 4, 1.5, 33]],
  },
  desktop: {
    font: 48,
    box: [-15.3, -6, 296, 53],
    word: 265.39,
    handles: [[0, 0, 2, 53], [-2, 47, 6, 6], [292, 0, 6, 6], [294, 0, 2, 52]],
  },
} satisfies Record<string, SelectionGeometry>;

/** Mobile carousel: one card (197.95px) plus the gap (7.92px) per step, in rem. */
const SLIDE_STEP = 25.7333;
const SLIDE_EVERY = 3200;
const PLACES = [1, 2, 3] as const;

export default function TradingCompetitionLeaderboard({ data }: { data: PageTradingCompetitionSchema }) {
  const sectionRef = useRef<HTMLElement>(null);
  const reduced = useReducedMotion();
  const [visible, setVisible] = useState(false);
  const running = visible && !reduced;
  // Live = the real board: sharp, searchable, no countdown. It switches on by itself once the
  // competition has standings (World Class Edge API via /api/competition/*). `?test-leaderboard`
  // still previews it with dummy data.
  const preview = useLeaderboardPreview();
  const apiBoard = useApiBoard(!preview);
  const live = preview || apiBoard !== null;

  // Everything moves only while the section is on screen.
  useEffect(() => {
    const el = sectionRef.current;
    if (!el) return;
    const io = new IntersectionObserver(([entry]) => setVisible(entry.isIntersecting), { rootMargin: "100px" });
    io.observe(el);
    return () => io.disconnect();
  }, []);

  // Entrance: the header rises and "Leaderboard" gets selected; the podium and the table start far
  // blurrier than drawn and ease into the design's blur — a deliberate "almost readable" tease.
  const [headerRef, headerReveal] = useReveal();
  const [podiumRef, podiumReveal] = useReveal({ threshold: 0.15 });
  const [tableRef, tableReveal] = useReveal({ threshold: 0.15 });

  const traders = data.leaderboardTraders ?? [];
  const prizes = traders.map((t) => t.prize);
  // the pre-launch tease drifts; the live board shows the standings as they are
  const { rows, me } = useLiveBoard(traders, data.leaderboardYou, running && !live);
  const dummy = useMemo(() => (preview ? createDummyBoard(traders) : null), [preview, traders]);
  const board = dummy ?? apiBoard;
  const bands = data.prizesBands ?? [];

  // Mobile carousel: loops over a doubled strip, then jumps back without a transition.
  const [slide, setSlide] = useState(0);
  const [instant, setInstant] = useState(false);
  useEffect(() => {
    if (!running) return;
    const id = window.setInterval(() => {
      setInstant(false);
      setSlide((s) => s + 1);
    }, SLIDE_EVERY);
    return () => window.clearInterval(id);
  }, [running]);
  const onSlideEnd = () => {
    if (slide >= PLACES.length) {
      setInstant(true);
      setSlide(0);
    }
  };

  const columns = data.leaderboardColumns;
  const units = data.leaderboardCountdownUnits;
  const ctaHref = useCtaHref(data.leaderboardCta, data.settings?.language);

  const card = (place: 1 | 2 | 3, className?: string, key?: string) => {
    const t = board ? board.top[place - 1] : rows[place - 1];
    if (!t) return null;
    const prize = board ? prizeForRank(bands, place) : undefined;
    return (
      <PodiumCard
        key={key ?? place}
        place={place}
        // live: first name only, like the table rows; pre-launch: the CMS sample name
        name={board ? maskName(t.name) : t.name}
        points={"total" in t ? publicPoints(t) : t.points}
        lastDay={t.lastDay}
        prize={board ? prize && formatPrize(prize.amount) : prizes[place - 1]}
        statue={data.leaderboardStatues?.[place - 1]}
        lastDayLabel={columns?.lastDay}
        prizeLabel={columns?.prize}
        className={className}
      />
    );
  };

  return (
    // mobile: Figma's frame is a fixed 888px (+10px for the wider gap above the button) and cuts the table off at the bottom
    <section
      ref={sectionRef}
      id="leaderboard"
      data-paused={running ? undefined : ""}
      className={cn(
        "relative flex scroll-mt-[7.5rem] flex-col items-center gap-[4rem] overflow-hidden bg-[linear-gradient(180deg,#001b14_0%,#00140f_30%,#00140f_100%)] px-[2rem] pt-[7rem] pb-[8rem] lg:gap-[6rem] lg:px-0 lg:py-[12.5rem]",
        !live && "h-[112.25rem] lg:h-auto",
      )}>
      {/* ── Header ─────────────────────────────────────────────────────── */}
      <div ref={headerRef} data-reveal={headerReveal} className="flex w-full flex-col items-center gap-[1.75rem] lg:w-auto lg:gap-[2rem]">
        {data.leaderboardChip && (
          <p style={at(0)} className="tc-rv-rise mb-0 font-blacker text-[2.125rem] leading-[0.9444] font-normal whitespace-nowrap text-[rgba(214,238,231,0.7)] italic lg:text-[2rem] lg:leading-[1.5]">
            {data.leaderboardChip}
          </p>
        )}

        <h2 style={at(0.12, 1)} className="tc-rv-rise mb-0 translate-x-[0.125rem] text-center font-geist text-[4.25rem] leading-[1.04] font-medium tracking-[-0.1913rem] whitespace-pre text-white lg:translate-x-0 lg:text-[6rem] lg:leading-[1.15] lg:tracking-[-0.3rem]">
          {data.leaderboardTitleBefore}
          {/* Figma's own spacing before the highlight: two spaces on mobile, three on desktop */}
          <span className="lg:hidden">{"  "}</span>
          <span className="hidden lg:inline">{"   "}</span>
          <span className="relative font-blacker leading-[0] font-normal text-[#bae9d6] italic lg:tracking-[-0.18rem] lg:text-[#d6eee7]">
            <SelectionHighlight
              {...TITLE_SELECTION}
              fill="rgba(218,255,245,0.12)"
              mobileFill="rgba(186,233,214,0.1)"
              handle="#bae9d6"
              delay={0.85}
            />
            <span className="relative font-blacker">{data.leaderboardTitleHighlight}</span>
          </span>
        </h2>

        {data.leaderboardDescription && (
          <p style={at(0.3)} className="tc-rv-rise mb-0 w-[49.75rem] max-w-full text-center font-geist text-[2rem] leading-[1.5] font-normal text-[rgba(214,238,231,0.66)] lg:w-[54.25rem] lg:text-[2.25rem]">
            {/* a line break in the copy applies to mobile only, as in the design */}
            <BreakLines text={data.leaderboardDescription} on="mobile" />
          </p>
        )}

        {data.leaderboardCta?.text && (
          <a
            href={ctaHref}
            style={at(0.42)}
            className="tc-rv-rise mt-[1.25rem] flex h-[6rem] w-full items-center justify-center overflow-hidden rounded-[0.25rem] border border-solid border-white/10 bg-white px-[2rem] py-[1rem] lg:mt-0 lg:h-[5.625rem] lg:w-auto">
            <span className={cn("font-geist text-[1.75rem] leading-[normal] font-medium whitespace-nowrap text-black", CAP_TRIM)}>
              {data.leaderboardCta.text}
            </span>
          </a>
        )}
      </div>

      {/* ── Podium: three statues, blurred until launch. A looping carousel on mobile. ── */}
      <div aria-hidden={live ? undefined : true} className="flex w-full flex-col items-start gap-[2rem] lg:w-[150rem] lg:items-center">
        <div
          ref={podiumRef}
          data-reveal={podiumReveal}
          style={at(0, live ? undefined : 1.8)}
          className={cn(
            "w-full",
            live ? "tc-rv-rise" : "tc-rv-unblur [--tc-blur:0.66rem] [filter:blur(var(--tc-blur))] lg:[--tc-blur:1rem]",
          )}>
          <div
            onTransitionEnd={onSlideEnd}
            className="flex items-start gap-[0.98975rem] lg:!translate-x-0 lg:items-end lg:justify-center lg:gap-[3rem] lg:!transition-none"
            style={{
              transform: `translateX(-${slide * SLIDE_STEP}rem)`,
              transition: instant ? "none" : "transform 700ms cubic-bezier(0.22,0.61,0.36,1)",
            }}>
            {card(1, "lg:order-2")}
            {card(2, "lg:order-1")}
            {card(3, "lg:order-3")}
            {/* clones that let the mobile strip loop */}
            {PLACES.map((p) => card(p, "lg:hidden", `clone-${p}`))}
          </div>
        </div>

        <div className={cn("flex w-full items-center justify-center gap-[0.75rem] lg:hidden", !live && "blur-[0.7375rem]")}>
          {PLACES.map((p, i) => (
            <i
              key={p}
              className={cn(
                "h-[0.75rem] rounded-[0.375rem] transition-[width,background-color] duration-500",
                slide % PLACES.length === i ? "w-[2.25rem] bg-white" : "w-[0.75rem] bg-white/30",
              )}
            />
          ))}
        </div>
      </div>

      {/* ── Table: your row, the top ten, the note. Blurred until launch; searchable once live. ── */}
      {/* one stable element: the reveal observer keeps watching it when the board switches to live */}
      <div
        aria-hidden={board ? undefined : true}
        ref={tableRef}
        data-reveal={tableReveal}
        style={at(0, board ? undefined : 1.8)}
        className={cn(
          "flex w-full flex-col items-center",
          board
            ? "tc-rv-rise"
            : "tc-rv-unblur h-[44.75rem] gap-[2rem] overflow-hidden [--tc-blur:1rem] [filter:blur(var(--tc-blur))] lg:h-auto lg:w-[150rem]",
        )}>
        {board ? (
          <LiveBoard
            source={board}
            bands={bands}
            columns={columns}
            you={data.leaderboardYou}
            note={data.leaderboardNote}
            language={data.settings?.language}
          />
        ) : (
          <LeaderboardTable
            rows={rows}
            prizes={prizes}
            you={data.leaderboardYou}
            live={me}
            columns={columns}
            note={data.leaderboardNote}
          />
        )}
      </div>

      {!live && (
        <TradingCompetitionCountdown
          variant="light"
          label={data.countdownLabel}
          target={data.countdownTarget}
          unitLabels={[units?.days, units?.hours, units?.minutes, units?.seconds]}
          columns={[51, 68, 64, 49]}
          className="top-[55.385rem] left-[calc(50%-24.875rem)] z-10 text-[0.11006rem] lg:top-[99.375rem] lg:left-1/2 lg:-translate-x-1/2"
          ownReveal
          revealDelay={0.25}
        />
      )}

      <style jsx global>{`
        /* centred on the Figma position: 5px down to 5px up */
        @keyframes tc-float {
          from { transform: translateY(5em); }
          to { transform: translateY(-5em); }
        }
        @keyframes tc-statue {
          from { transform: scale(1); }
          to { transform: scale(1.06) translateY(-1.5%); }
        }
        @keyframes tc-bar {
          0%, 100% { opacity: 0.35; transform: scaleX(0.6); }
          50% { opacity: 1; transform: scaleX(1); }
        }
        .tc-float { animation: tc-float 5.5s ease-in-out infinite alternate; }
        .tc-statue { animation: tc-statue 9s ease-in-out infinite alternate; }
        .tc-bar { animation: tc-bar 3.6s ease-in-out infinite; }
        [data-paused] .tc-anim { animation-play-state: paused; }
        @media (prefers-reduced-motion: reduce) {
          .tc-anim { animation: none; }
        }
      `}</style>
    </section>
  );
}
